import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Send, FlaskConical, Lock, Sparkles, Check, ArrowRight, Paperclip,
  X as XIcon, LayoutGrid, ChevronDown, BookmarkPlus, ShieldAlert,
  Loader2, Image as ImageIcon, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { AI_COACH } from "@/constants/testIds";
import Watermark from "@/components/Watermark";
import api, { API_BASE } from "@/lib/api";
import usePromoStatus from "@/lib/usePromoStatus";

// Popular instruments — surface as quick-picks so members don't type
// them wrong. The dropdown still allows free entry via the "Custom" input.
const SYMBOL_QUICK = [
  "XAUUSD", "XAGUSD", "NAS100", "SPX500", "US30", "GER40",
  "DXY", "EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCAD",
  "BTCUSD", "ETHUSD", "USOIL", "UKOIL",
];

// Whiteboard timeframes — in strict order (Monthly → H4). Extra chart
// slots are appended via "+Add chart".
const WHITEBOARD_SLOTS = [
  { key: "monthly", labelKey: "aiCoachUi2.monthly" },
  { key: "weekly",  labelKey: "aiCoachUi2.weekly"  },
  { key: "daily",   labelKey: "aiCoachUi2.daily"   },
  { key: "h4",      labelKey: "aiCoachUi2.h4"      },
];

const MAX_UPLOAD_MB = 5;

export default function AICoach() {
  const { t } = useTranslation();
  const { params: P, loyaltyOn } = usePromoStatus();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // {id, url, kind, filename, slot}
  const [whiteboardMode, setWhiteboardMode] = useState(false);
  const [symbol, setSymbol] = useState("");
  const [symbolCustom, setSymbolCustom] = useState("");
  const [prefs, setPrefs] = useState({ disclaimer_accepted_at: null, coach_mode: true, professor_retention_days: "45" });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [sessionAckd, setSessionAckd] = useState(false);       // per-session acknowledgment
  const [sessionAckChecked, setSessionAckChecked] = useState(false);  // checkbox state before saving
  const [socraticActive, setSocraticActive] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null); // { messageId, content }
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const isPremium = user?.tier === "premium" || user?.role === "admin" || !!user?.professor_addon_active;
  const isBasic = user?.tier === "basic";

  const renderInline = (text) => {
    const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
    return tokens.map((tok, i) => {
      if (tok.startsWith("**") && tok.endsWith("**"))
        return <strong key={i}>{tok.slice(2, -2)}</strong>;
      if (tok.startsWith("*") && tok.endsWith("*") && tok.length > 2)
        return <em key={i}>{tok.slice(1, -1)}</em>;
      return tok;
    });
  };

  const suggestions = t("aiCoachUi2.suggestions", { returnObjects: true });
  const suggestionsList = Array.isArray(suggestions) ? suggestions : [];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Fetch prefs on mount (Premium members only — the endpoint requires it).
  useEffect(() => {
    if (!isPremium) return;
    (async () => {
      try {
        const r = await api.get("/ai-coach/prefs");
        setPrefs(r.data);
        if (!r.data.disclaimer_accepted_at) setShowDisclaimer(true);
      } catch (e) {
        // Non-fatal: prefs default and the disclaimer will show next mount.
      } finally {
        setPrefsLoaded(true);
      }
    })();
    // eslint-disable-next-line
  }, [isPremium]);

  if (!isPremium) {
    // (Unchanged locked / basic-tier UI preserved from prior build.)
    if (isBasic) {
      return (
        <div
          data-testid={AI_COACH.basicNudge}
          className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-950 via-slate-950 to-blue-950 p-10 text-white"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl" />
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center mb-10">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-4">
                  <FlaskConical size={14} /> {t("aiCoach.professorName")}
                </div>
                <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-tight">
                  {t("aiCoachUi.basicNudgeTitle")}
                </h1>
                <p className="mt-4 text-slate-300 text-sm max-w-xl leading-relaxed">
                  {t("aiCoachUi.basicNudgeSubtitle")}
                </p>
              </div>
              <div className="flex flex-col items-center gap-3 shrink-0">
                <div className="w-20 h-20 relative">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-300 via-amber-500 to-orange-500 shadow-lg shadow-amber-500/40" />
                  <div className="absolute inset-[3px] rounded-[14px] bg-slate-950 flex items-center justify-center">
                    <span className="font-display font-black text-amber-400 text-3xl">P</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative rounded-xl bg-white text-slate-900 p-6 flex flex-col overflow-hidden ring-2 ring-amber-400">
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
                  <Sparkles size={9} /> {t("aiCoachUi.recommended")}
                </span>
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{t("aiCoachUi.upgradeLabel")}</div>
                <h3 className="mt-2 font-display font-bold text-xl leading-tight">{t("aiCoachUi.upgradeHeading")}</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700 flex-1">
                  <li className="flex gap-2"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {t("aiCoachUi.upgradeBenefit1")}</li>
                  <li className="flex gap-2"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {t("aiCoachUi.upgradeBenefit2")}</li>
                  <li className="flex gap-2"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {t("aiCoachUi.upgradeBenefit3")}</li>
                  <li className="flex gap-2"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" /> {t("aiCoachUi.upgradeBenefit4")}</li>
                </ul>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display font-black text-3xl tracking-tight text-slate-900">{P.upgrade}</span>
                  {loyaltyOn && <span className="text-slate-500 text-sm">{t("aiCoachUi.upgradeDelta")}</span>}
                </div>
                <Link to="/pricing?src=professor-basic-upgrade" data-testid={AI_COACH.basicUpgradeCta} className="mt-4">
                  <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold h-11">
                    {t("aiCoachUi.upgradeCta")} <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-6 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{t("aiCoachUi.addOnLabel")}</div>
                <h3 className="mt-2 font-display font-bold text-xl leading-tight text-white">{t("aiCoachUi.addOnHeading")}</h3>
                <p className="mt-3 text-sm text-slate-400 flex-1">
                  {t("aiCoachUi.addOnBody")}
                </p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display font-black text-3xl tracking-tight text-amber-400">$100</span>
                  <span className="text-slate-500 text-sm">{t("aiCoachUi.perMonth")}</span>
                </div>
                <Link to="/shop" data-testid={AI_COACH.basicNudgeCta} className="mt-4">
                  <Button variant="outline" className="w-full border-slate-600 text-white bg-transparent hover:bg-slate-800 h-11">
                    {t("aiCoachUi.basicNudgeCta")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div
          data-testid={AI_COACH.lockedNotice}
          className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center text-white relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/15 text-amber-400 mb-6 border border-amber-500/30">
              <Lock size={22} />
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight">{t("aiCoachUi.lockedTitle")}</h1>
            <p className="mt-3 text-slate-300 max-w-md mx-auto">{t("aiCoachUi.lockedBody")}</p>
            <Link to="/pricing">
              <Button className="mt-8 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-11 px-6">
                <Sparkles size={16} className="mr-2" />
                {t("aiCoachUi.lockedUpgradeCta")}
              </Button>
            </Link>
          </div>
        </div>

        <div data-testid="ai-coach-funnel-preview" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {["chartReviews", "whiteboardMode", "journalCritique", "playbookCoaching"].map((k) => (
            <div key={k} className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow group">
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-amber-100 group-hover:text-amber-700 transition-colors">
                <Lock size={14} />
              </div>
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
                <FlaskConical size={20} />
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 tracking-tight">{t(`aiCoachUi.cards.${k}.title`)}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(`aiCoachUi.cards.${k}.body`)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- Attachment helpers ------------------------------------------------

  const uploadFile = async (file, slot = null) => {
    if (!file) return null;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(t("aiCoachUi2.fileTooLarge", { max: MAX_UPLOAD_MB }));
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/ai-coach/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const item = { ...r.data, slot };
      setAttachments((prev) => {
        // If slot is a Whiteboard TF, replace any existing chart in that slot
        if (slot && ["monthly", "weekly", "daily", "h4"].includes(slot)) {
          return [...prev.filter((a) => a.slot !== slot), item];
        }
        return [...prev, item];
      });
      return item;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("aiCoachUi2.uploadFailed"));
      return null;
    }
  };

  const removeAttachment = async (att) => {
    setAttachments((prev) => prev.filter((a) => a.id !== att.id));
    try { await api.delete(`/ai-coach/uploads/${att.id}`); } catch (_e) { /* ignore */ }
  };

  const onPickFiles = async (e, slot = null) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) await uploadFile(f, slot);
  };

  // --- Prefs -------------------------------------------------------------

  const patchPref = async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    try { await api.patch("/ai-coach/prefs", patch); } catch (_e) { /* ignore */ }
  };

  const acceptDisclaimer = async () => {
    try {
      await api.post("/ai-coach/disclaimer/accept");
      setPrefs((p) => ({ ...p, disclaimer_accepted_at: new Date().toISOString() }));
      setShowDisclaimer(false);
    } catch (_e) {
      toast.error(t("aiCoachUi2.disclaimerSaveError"));
    }
  };

  // --- Send message ------------------------------------------------------

  const sendMessage = async (text, opts = {}) => {
    const trimmed = (text ?? input).trim();
    if ((!trimmed && attachments.length === 0) || sending) return;
    // Per-session disclaimer must be acknowledged before ANY send.
    if (!sessionAckd) {
      toast.error(t("aiCoachUi2.ackRequired"));
      return;
    }

    const finalSymbol = symbol === "__custom__" ? symbolCustom.trim() : symbol;
    const now = Date.now();
    const attachSnapshot = attachments.slice();
    setMessages((m) => [
      ...m,
      {
        id: `u-${now}`, role: "user", content: trimmed,
        attachments: attachSnapshot, symbol: finalSymbol,
        whiteboard: whiteboardMode,
      },
      { id: `a-${now}`, role: "assistant", content: "", pending: true },
    ]);
    setInput("");
    setSending(true);
    setSocraticActive(false);

    // Clear attachment tray + reset whiteboard/symbol after send.
    const usedWhiteboard = whiteboardMode;
    const usedAttachments = attachSnapshot;
    setAttachments([]);
    // Keep symbol sticky if user typed custom; reset dropdown either way for next turn
    setSymbol("");
    setSymbolCustom("");

    let assistantMsgId = null;

    try {
      let resp = await fetch(`${API_BASE}/ai-coach/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: trimmed,
          attachments: usedAttachments.map((a) => ({ upload_id: a.id, slot: a.slot || null })),
          symbol: finalSymbol || null,
          whiteboard_mode: usedWhiteboard,
          bypass_socratic: !!opts.bypassSocratic,
        }),
      });
      if (resp.status === 401) {
        await api.post("/auth/refresh");
        resp = await fetch(`${API_BASE}/ai-coach/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            message: trimmed,
            attachments: usedAttachments.map((a) => ({ upload_id: a.id, slot: a.slot || null })),
            symbol: finalSymbol || null,
            whiteboard_mode: usedWhiteboard,
            bypass_socratic: !!opts.bypassSocratic,
          }),
        });
      }
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          let event = "message";
          let dataParts = [];
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) dataParts.push(line.slice(6));
          }
          const data = dataParts.join("\n");
          if (event === "session") {
            const newSid = data.trim();
            setSessionId(newSid);
            // Persist the per-session risk acknowledgment we captured
            // locally BEFORE the first send. Fire-and-forget — the SSE
            // stream already succeeded, so a network hiccup here just
            // means the next turn will be blocked by the 428 and we'll
            // reprompt.
            (async () => {
              try { await api.post(`/ai-coach/sessions/${newSid}/acknowledge`); }
              catch (_e) { /* non-fatal */ }
            })();
          } else if (event === "session_created") {
            /* no-op — kept for future analytics */
          } else if (event === "mode") {
            if (data.trim() === "socratic") setSocraticActive(true);
          } else if (event === "message_id") {
            assistantMsgId = data.trim();
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, id: `a-server-${assistantMsgId}`, serverId: assistantMsgId, pending: false, whiteboard: usedWhiteboard };
              return next;
            });
          } else if (event === "error") {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { ...next[next.length - 1], content: `⚠️ ${data}`, pending: false };
              return next;
            });
          } else if (event !== "done" && event !== "session_created") {
            const decoded = data.replace(/\\n/g, "\n");
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: (last.content || "") + decoded };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: `⚠️ ${e.message}`, pending: false };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const bypassSocratic = () => {
    // Re-send the last user turn with bypass flag — pulls the previous
    // attachments back in so Claude doesn't lose context.
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    // Restore attachments + fire again
    setAttachments(lastUser.attachments || []);
    setWhiteboardMode(!!lastUser.whiteboard);
    setSymbol(lastUser.symbol || "");
    // Small tick so state applies
    setTimeout(() => sendMessage(lastUser.content || "Give me your read.", { bypassSocratic: true }), 50);
  };

  const openSaveDialog = (m) => {
    setSaveDialog({
      messageId: m.serverId,
      content: m.content,
      instrument: symbol || "",
      direction: "long",
      entry: "",
      stop: "",
      target: "",
      notes: "",
    });
  };

  const submitSaveDialog = async () => {
    if (!saveDialog?.messageId || !sessionId) return;
    try {
      await api.post("/ai-coach/save-to-journal", {
        session_id: sessionId,
        message_id: saveDialog.messageId,
        instrument: saveDialog.instrument || null,
        direction: saveDialog.direction || null,
        entry: saveDialog.entry ? parseFloat(saveDialog.entry) : null,
        stop: saveDialog.stop ? parseFloat(saveDialog.stop) : null,
        target: saveDialog.target ? parseFloat(saveDialog.target) : null,
        notes: saveDialog.notes || null,
      });
      toast.success(t("aiCoachUi2.savedToJournal"));
      setSaveDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("aiCoachUi2.saveError"));
    }
  };

  const filledSlots = attachments.filter((a) => WHITEBOARD_SLOTS.some((s) => s.key === a.slot));
  const extraCharts = attachments.filter((a) => a.slot === "extra");
  const freeAttachments = attachments.filter((a) => !a.slot);

  // -----------------------------------------------------------------------
  return (
    <div data-testid={AI_COACH.root} className="flex flex-col h-[calc(100vh-10rem)] -mx-6 sm:-mx-10 lg:-mx-16 px-6 sm:px-10 lg:px-16 pt-6 relative">
      <Watermark fullscreen />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 rounded-2xl"
        style={{
          backgroundColor: "#0B1220",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      <div className="absolute -top-10 right-16 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

      {/* Header + Coach-mode toggle */}
      <div className="relative flex flex-wrap items-start justify-between gap-4 mb-4 pt-2">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold mb-2">
            <FlaskConical size={14} /> {t("aiCoachUi.labLabel")}
          </div>
          <h1 className="font-display font-bold text-3xl text-white tracking-tight">
            {t("aiCoachUi.labWelcome")}
          </h1>
          <p className="mt-1 text-sm text-slate-400 max-w-lg">{t("aiCoachUi.labSubtitle")}</p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {/* Coach Mode toggle */}
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 select-none cursor-pointer">
            <Switch
              data-testid={AI_COACH.coachModeToggle}
              checked={!!prefs.coach_mode}
              onCheckedChange={(v) => patchPref({ coach_mode: !!v })}
            />
            <span className="uppercase tracking-widest">{t("aiCoachUi2.coachMode")}</span>
          </label>
          {/* Professor avatar */}
          <div className="hidden sm:flex flex-col items-center gap-2 shrink-0">
            <div className="w-16 h-16 relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-300 via-amber-500 to-orange-500 shadow-lg shadow-amber-500/40" />
              <div className="absolute inset-[3px] rounded-[14px] bg-slate-950 flex items-center justify-center">
                <span className="font-display font-black text-amber-400 text-2xl">P</span>
              </div>
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-slate-950 shadow-[0_0_10px_2px_rgba(52,211,153,0.6)]" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              {t("aiCoachUi2.professorName")}
            </div>
          </div>
        </div>
      </div>

      {/* Per-session risk acknowledgment banner — required on every NEW conversation */}
      {prefsLoaded && prefs.disclaimer_accepted_at && !sessionAckd && (
        <div className="relative mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 backdrop-blur p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0 mt-0.5">
              <ShieldAlert size={16} className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-1">
                {t("aiCoachUi2.sessionAckTitle")}
              </div>
              <p className="text-sm text-slate-200 leading-relaxed">
                {t("aiCoachUi2.sessionAckMessage")}
              </p>
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  data-testid="ai-coach-session-ack-checkbox"
                  checked={sessionAckChecked}
                  onChange={(e) => {
                    setSessionAckChecked(e.target.checked);
                    if (e.target.checked) {
                      setSessionAckd(true);
                      // If session already exists (rare — usually only after first send),
                      // persist immediately. First-send flow acknowledges on `session` event.
                      if (sessionId) {
                        api.post(`/ai-coach/sessions/${sessionId}/acknowledge`).catch(() => {});
                      }
                    } else {
                      setSessionAckd(false);
                    }
                  }}
                  className="w-4 h-4 rounded border-2 border-amber-500/60 bg-transparent accent-amber-500 cursor-pointer"
                />
                <span className="text-xs font-semibold text-amber-200 group-hover:text-amber-100">
                  {t("aiCoachUi2.sessionAckCheckbox")}
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid={AI_COACH.messagesContainer}
        className="relative flex-1 overflow-y-auto pb-4 space-y-4"
      >
        {messages.length === 0 && (
          <div className="text-slate-400 space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-2xl">
              <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-2">
                {t("aiCoachUi2.professorName")}
              </div>
              <p className="text-slate-200 leading-relaxed">
                {t("aiCoachUi2.introMessage")}
              </p>
            </div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold pt-2">
              {t("aiCoachUi2.experiments")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {suggestionsList.map((s, i) => (
                <button
                  key={s}
                  data-testid={`${AI_COACH.suggestion}-${i}`}
                  onClick={() => sendMessage(s)}
                  className="text-left p-4 border border-white/10 bg-white/5 rounded-lg hover:border-amber-400 hover:bg-amber-500/5 text-slate-200 text-sm transition-colors"
                >
                  <span className="text-amber-400 font-mono text-xs mr-2">0{i + 1}</span>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={m.id || `msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="w-9 h-9 rounded-lg mr-3 shrink-0 relative">
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-300 to-amber-600" />
                <div className="absolute inset-[2px] rounded-md bg-slate-950 flex items-center justify-center">
                  <FlaskConical size={14} className="text-amber-400" />
                </div>
              </div>
            )}
            <div className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-blue-700 text-white rounded-tr-sm"
                : "bg-white/8 backdrop-blur text-slate-100 border border-white/10 rounded-tl-sm"
            }`}>
              {/* Attachment thumbs on user messages */}
              {m.role === "user" && m.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {m.attachments.map((a) => (
                    <div key={a.id} className="relative rounded-md overflow-hidden border border-white/25 bg-black/30">
                      {a.kind === "image" ? (
                        <img src={`${API_BASE}${a.url}`} alt={a.filename} className="w-16 h-16 object-cover" />
                      ) : (
                        <div className="w-16 h-16 flex flex-col items-center justify-center text-white/80 text-[9px] p-1 text-center">
                          <FileText size={16} className="mb-1" />
                          <span className="truncate w-full">{a.filename}</span>
                        </div>
                      )}
                      {a.slot && (
                        <span className="absolute bottom-0 left-0 right-0 text-[8px] uppercase tracking-widest font-bold text-amber-300 bg-black/70 px-1 py-0.5 text-center">
                          {a.slot}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {m.content ? renderInline(m.content) : (
                <span className="text-slate-400 italic">{t("aiCoachUi2.thinking")}</span>
              )}

              {/* Assistant actions: Skip Socratic + Save to Journal */}
              {m.role === "assistant" && !m.pending && m.content && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2">
                  {socraticActive && i === messages.length - 1 && (
                    <Button
                      data-testid={AI_COACH.socraticSkip}
                      size="sm"
                      variant="outline"
                      onClick={bypassSocratic}
                      className="h-8 text-[11px] border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200"
                    >
                      {t("aiCoachUi2.skipSocratic")}
                    </Button>
                  )}
                  {m.serverId && (m.whiteboard || /BIAS:/i.test(m.content)) && (
                    <Button
                      data-testid={`${AI_COACH.saveToJournal}-${m.serverId}`}
                      size="sm"
                      variant="outline"
                      onClick={() => openSaveDialog(m)}
                      className="h-8 text-[11px] border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200"
                    >
                      <BookmarkPlus size={12} className="mr-1.5" />
                      {t("aiCoachUi2.saveAsTradeCard")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Whiteboard Mode panel */}
      {whiteboardMode && (
        <div className="relative mb-3 rounded-xl border border-amber-500/25 bg-slate-900/70 backdrop-blur p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
              {t("aiCoachUi2.whiteboardModeTitle")}
            </div>
            <button
              type="button"
              onClick={() => { setWhiteboardMode(false); setAttachments((prev) => prev.filter((a) => !a.slot || a.slot === null)); }}
              className="text-slate-400 hover:text-white text-xs"
            >
              <XIcon size={14} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {WHITEBOARD_SLOTS.map((slot) => {
              const filled = filledSlots.find((a) => a.slot === slot.key);
              return (
                <label
                  key={slot.key}
                  data-testid={`${AI_COACH.whiteboardSlot}-${slot.key}`}
                  className={`relative aspect-video rounded-md border-2 border-dashed flex flex-col items-center justify-center text-center p-2 cursor-pointer transition-colors ${
                    filled
                      ? "border-emerald-500/60 bg-emerald-500/10"
                      : "border-slate-600 bg-slate-950/60 hover:border-amber-500/50"
                  }`}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    onChange={(e) => onPickFiles(e, slot.key)}
                  />
                  {filled ? (
                    <>
                      <img src={`${API_BASE}${filled.url}`} alt={slot.label} className="absolute inset-0 w-full h-full object-cover rounded-md opacity-80" />
                      <div className="relative z-10 bg-black/60 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                        {t(slot.labelKey)}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); removeAttachment(filled); }}
                        className="absolute top-1 right-1 z-20 w-5 h-5 rounded-full bg-black/70 text-white hover:bg-red-500 flex items-center justify-center"
                        aria-label={t("aiCoachUi2.removeSlot", { label: t(slot.labelKey) })}
                      >
                        <XIcon size={10} />
                      </button>
                    </>
                  ) : (
                    <>
                      <ImageIcon size={16} className="text-slate-500 mb-1" />
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{t(slot.labelKey)}</div>
                      <div className="text-[9px] text-slate-500">{t("aiCoachUi2.tapToAttach")}</div>
                    </>
                  )}
                </label>
              );
            })}
          </div>
          {/* Extra charts */}
          {extraCharts.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {extraCharts.map((a) => (
                <div key={a.id} className="relative w-14 h-14 rounded overflow-hidden border border-slate-700">
                  <img src={`${API_BASE}${a.url}`} alt={a.filename} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAttachment(a)}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white hover:bg-red-500 flex items-center justify-center"
                  >
                    <XIcon size={8} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 hover:text-amber-400 mt-2 cursor-pointer">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => onPickFiles(e, "extra")}
            />
            <Paperclip size={11} /> {t("aiCoachUi2.addContextChart")}
          </label>
        </div>
      )}

      {/* Attachment tray (non-Whiteboard) */}
      {!whiteboardMode && attachments.length > 0 && (
        <div className="relative mb-2 flex flex-wrap gap-2">
          {attachments.map((a) => (
            <div
              key={a.id}
              data-testid={AI_COACH.attachChip}
              className="relative flex items-center gap-2 rounded-md border border-white/15 bg-slate-900/70 backdrop-blur px-2 py-1.5 text-xs text-slate-200"
            >
              {a.kind === "image" ? (
                <img src={`${API_BASE}${a.url}`} alt={a.filename} className="w-8 h-8 object-cover rounded" />
              ) : (
                <FileText size={14} className="text-slate-400" />
              )}
              <span className="max-w-[10rem] truncate">{a.filename}</span>
              <button
                type="button"
                data-testid={AI_COACH.attachRemove}
                onClick={() => removeAttachment(a)}
                className="text-slate-400 hover:text-red-400"
                aria-label={t("aiCoachUi2.removeAttachment")}
              >
                <XIcon size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input row */}
      <form
        onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
        className="relative bg-white/5 border border-white/15 backdrop-blur-xl rounded-2xl p-3 space-y-2 shadow-2xl mb-4"
      >
        {/* Tool row: Whiteboard toggle, Symbol quick-pick, Attach */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid={AI_COACH.whiteboardToggle}
            onClick={() => setWhiteboardMode((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest border transition-colors ${
              whiteboardMode
                ? "border-amber-400 text-amber-300 bg-amber-500/15"
                : "border-white/20 text-slate-300 hover:border-amber-400/50 hover:text-amber-300"
            }`}
          >
            <LayoutGrid size={12} /> {t("aiCoachUi2.whiteboard")}
          </button>

          {/* Symbol quick-pick */}
          <div className="relative">
            <select
              data-testid={AI_COACH.symbolSelect}
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="appearance-none h-8 pl-3 pr-8 rounded-full text-[11px] font-semibold uppercase tracking-widest border border-white/20 bg-slate-900/70 text-slate-200 hover:border-amber-400/50 cursor-pointer"
            >
              <option value="">{t("aiCoachUi2.symbolPlaceholder")}</option>
              {SYMBOL_QUICK.map((s) => <option key={s} value={s}>{s}</option>)}
              <option value="__custom__">{t("aiCoachUi2.customSymbol")}</option>
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          {symbol === "__custom__" && (
            <Input
              value={symbolCustom}
              onChange={(e) => setSymbolCustom(e.target.value.toUpperCase())}
              placeholder={t("aiCoachUi2.customSymbolPlaceholder")}
              className="h-8 w-28 text-[11px] font-semibold bg-slate-900/70 border-white/20 text-white uppercase"
              maxLength={12}
            />
          )}

          {/* Freeform attach */}
          {!whiteboardMode && (
            <>
              <button
                type="button"
                data-testid={AI_COACH.attachButton}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest border border-white/20 text-slate-300 hover:border-amber-400/50 hover:text-amber-300"
              >
                <Paperclip size={12} /> {t("aiCoachUi2.attach")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                data-testid={AI_COACH.attachInput}
                accept="image/png,image/jpeg,image/webp,application/pdf,text/plain,text/markdown,.md"
                multiple
                className="sr-only"
                onChange={(e) => onPickFiles(e, null)}
              />
            </>
          )}
          <div className="grow" />
          <div className="text-[10px] text-slate-500 hidden sm:block">
            {whiteboardMode
              ? t("aiCoachUi2.whiteboardHint")
              : t("aiCoachUi2.attachHint")}
          </div>
        </div>

        <div className="flex items-end gap-3">
          <Textarea
            data-testid={AI_COACH.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              whiteboardMode
                ? t("aiCoachUi2.whiteboardPlaceholder")
                : t("aiCoachUi2.placeholder")
            }
            rows={1}
            className="resize-none border-0 focus-visible:ring-0 shadow-none bg-transparent min-h-[40px] text-slate-100 placeholder-slate-500"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage(input);
              }
            }}
          />
          <Button
            type="submit"
            data-testid={AI_COACH.sendButton}
            disabled={sending || !sessionAckd || (!input.trim() && attachments.length === 0)}
            title={!sessionAckd ? t("aiCoachUi2.sendDisabledTooltip") : undefined}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-10 w-10 p-0 rounded-full shrink-0 shadow-lg shadow-amber-500/30 disabled:opacity-40"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
      </form>

      {/* First-use disclaimer modal */}
      <Dialog open={showDisclaimer && prefsLoaded} onOpenChange={() => { /* forced acknowledgment */ }}>
        <DialogContent data-testid={AI_COACH.disclaimerModal} className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-3">
              <ShieldAlert size={22} />
            </div>
            <DialogTitle className="font-display text-xl">{t("aiCoachUi2.disclaimerTitle")}</DialogTitle>
            <DialogDescription className="pt-2 text-slate-600 leading-relaxed">
              {t("aiCoachUi2.disclaimerBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid={AI_COACH.disclaimerAccept} onClick={acceptDisclaimer} className="w-full bg-blue-900 hover:bg-blue-800 text-white">
              {t("aiCoachUi2.disclaimerAccept")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-to-journal dialog */}
      <Dialog open={!!saveDialog} onOpenChange={(v) => !v && setSaveDialog(null)}>
        <DialogContent data-testid={AI_COACH.saveDialog} className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("aiCoachUi2.saveDialogTitle")}</DialogTitle>
            <DialogDescription>{t("aiCoachUi2.saveDialogDescription")}</DialogDescription>
          </DialogHeader>
          {saveDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">{t("aiCoachUi2.instrument")}</Label>
                  <Input value={saveDialog.instrument} onChange={(e) => setSaveDialog({ ...saveDialog, instrument: e.target.value.toUpperCase() })} placeholder={t("aiCoachUi2.instrumentPlaceholder")} />
                </div>
                <div>
                  <Label className="text-xs font-semibold">{t("aiCoachUi2.direction")}</Label>
                  <select
                    value={saveDialog.direction}
                    onChange={(e) => setSaveDialog({ ...saveDialog, direction: e.target.value })}
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  >
                    <option value="long">{t("aiCoachUi2.long")}</option>
                    <option value="short">{t("aiCoachUi2.short")}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold">{t("aiCoachUi2.entry")}</Label>
                  <Input inputMode="decimal" value={saveDialog.entry} onChange={(e) => setSaveDialog({ ...saveDialog, entry: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-semibold">{t("aiCoachUi2.stop")}</Label>
                  <Input inputMode="decimal" value={saveDialog.stop} onChange={(e) => setSaveDialog({ ...saveDialog, stop: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs font-semibold">{t("aiCoachUi2.target")}</Label>
                  <Input inputMode="decimal" value={saveDialog.target} onChange={(e) => setSaveDialog({ ...saveDialog, target: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">{t("aiCoachUi2.notes")}</Label>
                <Textarea rows={3} value={saveDialog.notes} onChange={(e) => setSaveDialog({ ...saveDialog, notes: e.target.value })} placeholder={t("aiCoachUi2.notesPlaceholder")} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveDialog(null)}>{t("aiCoachUi2.cancel")}</Button>
            <Button data-testid={AI_COACH.saveDialogSubmit} onClick={submitSaveDialog} className="bg-emerald-600 hover:bg-emerald-500 text-white">
              <BookmarkPlus size={14} className="mr-1.5" /> {t("aiCoachUi2.saveTradeCard")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
